import { cn } from "@/lib/utils";

type SkeletonVariant =
  | "hero"
  | "grid"
  | "text"
  | "carousel"
  | "split"
  | "compact";

type SectionSkeletonProps = {
  variant?: SkeletonVariant;
  className?: string;
  /** Tom de fundo (combinar com a section real para evitar CLS de cor). */
  tone?: "white" | "cream" | "navy" | "light";
};

const toneClass: Record<NonNullable<SectionSkeletonProps["tone"]>, string> = {
  white: "bg-white",
  cream: "bg-[var(--sr-cream)]",
  navy: "bg-[var(--sr-navy-deep)]",
  light: "bg-gray-50",
};

const heightClass: Record<SkeletonVariant, string> = {
  hero: "min-h-[100svh]",
  grid: "min-h-[800px]",
  text: "min-h-[480px]",
  carousel: "min-h-[680px]",
  split: "min-h-[720px]",
  compact: "min-h-[420px]",
};

/**
 * Skeleton de section, usado como fallback de Suspense para lazy-loaded sections.
 * Garante CLS=0 ao reservar a altura aproximada e tom de fundo idênticos.
 * `aria-busy` informa screen readers que o conteúdo está carregando.
 */
export function SectionSkeleton({
  variant = "grid",
  className,
  tone = "white",
}: SectionSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn(
        "w-full py-[var(--space-section)] relative",
        heightClass[variant],
        toneClass[tone],
        className
      )}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 animate-pulse">
        <div className="mx-auto max-w-md space-y-3 mb-12">
          <div className="h-3 w-32 bg-black/10 rounded-full mx-auto" />
          <div className="h-8 w-64 bg-black/15 rounded mx-auto" />
          <div className="h-3 w-56 bg-black/10 rounded mx-auto" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="aspect-[4/3] bg-black/10 rounded-2xl" />
          <div className="aspect-[4/3] bg-black/10 rounded-2xl" />
          <div className="aspect-[4/3] bg-black/10 rounded-2xl" />
        </div>
      </div>
      <span className="sr-only">Carregando seção...</span>
    </div>
  );
}
